import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import gsap from 'gsap';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import Heading from 'components/atoms/heading/Heading';
import Text from 'components/atoms/text/Text';
import type { AutoAccordionProps, AutoAccordionRef } from './AutoAccordion.data';
import styles from './AutoAccordion.module.scss';

export default function AutoAccordion({
  blok,
  ref,
  ...props
}: AutoAccordionProps & { ref?: Ref<AutoAccordionRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeIndexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const items = blok.items ?? [];
  const pinTimeline = useMemo(() => gsap.timeline({ paused: true }), []);

  const setActiveItem = useCallback(
    (index: number) => {
      const nextIndex = Math.min(Math.max(index, 0), Math.max(items.length - 1, 0));

      if (activeIndexRef.current !== nextIndex) {
        activeIndexRef.current = nextIndex;
        setActiveIndex(nextIndex);
      }
    },
    [items.length],
  );

  const getPinnedWrapperY = useCallback(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return 0;

    return -Math.max(wrapper.offsetHeight - window.innerHeight, 0);
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    pinTimeline.clear();

    if (wrapper) {
      pinTimeline.fromTo(
        wrapper,
        { y: 0 },
        {
          y: getPinnedWrapperY,
          duration: 1,
          ease: 'none',
        },
      );
    }

    return () => {
      pinTimeline.clear();
      gsap.set(wrapper, { clearProps: 'transform' });
    };
  }, [getPinnedWrapperY, items.length, pinTimeline]);

  useEffect(() => {
    return () => {
      pinTimeline.kill();
    };
  }, [pinTimeline]);

  useEffect(() => {
    setActiveItem(activeIndexRef.current);
  }, [items.length, setActiveItem]);

  useScrollTrigger({
    trigger: elementRef,
    pin: elementRef,
    animation: pinTimeline,
    disabled: items.length === 0,
    vars: {
      start: 'top top',
      end: () => `+=${(Math.max(items.length, 1) * window.innerHeight) / 2}`,
      pinType: 'transform',
      anticipatePin: 1,
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        setActiveItem(Math.floor(self.progress * items.length));
      },
      onLeaveBack: () => {
        setActiveItem(0);
      },
    },
  });

  return (
    <section
      ref={elementRef}
      className={classNames(styles.autoAccordion, props.className)}
      {...sbEditable(blok)}
    >
      <div ref={wrapperRef} className={classNames(styles.wrapper, 'grid-gutter')}>
        {blok.title && (
          <Heading
            className={styles.title}
            as="h2"
            size="heading-03"
            transitionInType="splitRotateX3dIn"
            disableTriggerInOnScroll={false}
          >
            {blok.title}
          </Heading>
        )}

        <div className={styles.list}>
          {items.map((item, index) => {
            const isOpen = activeIndex === index;
            const description = item.long_description || item.short_description;

            return (
              <article
                className={classNames(styles.item, {
                  [styles.open]: isOpen,
                })}
                key={item._uid}
                onClick={() => setActiveItem(index)}
              >
                <button
                  type="button"
                  className={classNames(styles.header, 'button')}
                  aria-expanded={isOpen}
                  onClick={() => setActiveItem(index)}
                >
                  <span className={styles.index}>{`0${index + 1}`.slice(-2)}</span>

                  <Heading
                    className={styles.itemTitle}
                    as="h3"
                    size="heading-05"
                    transitionInType="none"
                    disableTriggerInOnScroll
                  >
                    {item.title}
                  </Heading>
                </button>

                <div className={styles.content} aria-hidden={!isOpen}>
                  {description && (
                    <Text
                      className={styles.description}
                      as="p"
                      size="body-large"
                      transitionInType="none"
                      disableTriggerInOnScroll
                    >
                      {description}
                    </Text>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
